import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { createCard } from "../../../../sevices/apiCards";
import FieldCreateEditCard from "./FieldCreateEditCard";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../hooks/useResponsive";
import useToastPromise from "../../../../hooks/useToast";
import { TooltipName } from "../../../../utils/Enums";
import { CustomButton } from "../../../../components/Button/Button";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";
import { isAdmin } from "../../../../utils/constants";

function CreateCard({
  stage,
  isTodo = false,
  newCreatedCardId,
  isCreateCardFieldOpen,
  handleCreateCardClick,
  parentCardId = null,
  boardName
}) {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [createBtnOpen, setCreateBtnOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [milestone, setMilestone] = useCustomSearchParams("milestone");
  const isMobile = useResponsive("down", "md");
  const boardPermission= boardName?.board_permission;
  
  // Use refs to persist state across tab switches
  const titleRef = React.useRef("");
  const createBtnOpenRef = React.useRef(false);
  const isTabVisibleRef = React.useRef(true);

  const { showToastPromise } = useToastPromise();

  const { mutate: createMutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(createCard, data);
    },
    onMutate: async (data) => {
      // Snapshot the previous value BEFORE canceling queries
      const previousCards = queryClient.getQueryData([`cards`, id]);
      
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [`cards`, id] });

      // Create optimistic card object matching the API response structure
      // Use a negative number for temp ID to avoid conflicts and make it easy to identify
      const tempCardId = -(Date.now());
      const optimisticCard = {
        card: {
          card_id: tempCardId,
          title: data.title,
          workflow_step_id: isTodo ? null : data.workflow_step_id,
          is_todo: isTodo || false,
          todo_column: isTodo ? "Open" : null,
          epic_id: data.epic_id || null,
          parent_card_id: data.parent_card_id || null,
          date_created: new Date().toISOString(),
          priority: "Medium",
          allocated_hours: null,
          description: null,
          due_date: null,
          reporter: null,
          assigned_to_user: null,
          card_sequence: -1, // Negative sequence to appear at top (before all other cards)
          board_id: id,
          card_type: null,
          label_ids: [],
          labels: [],
          can_exceed_allocated_hours: true,
          severity: 3,
          updated_at: new Date().toISOString(),
        },
        labels: [],
        sub_tasks: [],
        total_hours_spent: 0,
        board_permission: boardName?.board_permission || {},
        board_admin: boardName?.board_admin || null,
        board_name: boardName?.name || "",
        key: boardName?.key || "",
        assigned_user_last_seen: null,
        generic_step_id: null,
      };

      // Optimistically update the cache - ensure we preserve ALL existing cards
      queryClient.setQueryData([`cards`, id], (oldData) => {
        // Use previousCards if oldData is null/undefined (shouldn't happen, but safety check)
        const currentData = oldData || previousCards;
        
        // The structure is: data.data.data.responseData (nested data properties)
        const existingCards = currentData?.data?.data?.responseData || [];
        
        if (existingCards.length === 0 && !currentData) {
          // If no data exists at all, create a basic structure with just the optimistic card
          return {
            data: {
              data: {
                responseData: [optimisticCard],
              },
            },
          };
        }
        
        // IMPORTANT: Preserve ALL existing cards and add the new optimistic card
        // Make sure we're not losing any existing cards
        const newResponseData = [optimisticCard,...existingCards];
        
        return {
          ...currentData,
          data: {
            ...currentData.data,
            data: {
              ...currentData.data.data,
              responseData: newResponseData,
            },
          },
        };
      });

      // Keep the field open and show loading state - don't close until API succeeds
      // Don't clear title or close field here - let onSuccess handle it

      // Return context with the previous value and temp card id
      return { previousCards, tempCardId };
    },
    onSuccess: (res, variables, context) => {
      const newCardId = res?.data?.data?.responseData?.card?.card_id;
      newCreatedCardId?.(newCardId);

      // Update the optimistic card with the real card_id immediately
      queryClient.setQueryData([`cards`, id], (oldData) => {
        if (!oldData?.data?.data?.responseData) return oldData;
        
        // Update the temp card with the real card_id
        // Preserve ALL existing cards
        const updatedCards = oldData.data.data.responseData.map((card) => {
          if (card.card.card_id === context.tempCardId) {
            return {
              ...card,
              card: {
                ...card.card,
                card_id: newCardId,
              },
            };
          }
          return card;
        });
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              responseData: updatedCards,
            },
          },
        };
      });

      setLoading(false);
      setTitle("");
      setCreateBtnOpen(false);

      // Invalidate related queries that don't affect the card list display
      parentCardId &&
        queryClient.invalidateQueries({
          queryKey: [`subTasks`, parentCardId],
        });
      if (milestone) {
        queryClient.invalidateQueries({
          queryKey: ["epic_id", milestone],
        });
        queryClient.invalidateQueries({
          queryKey: ["milestone_history", milestone],
        });
      }

      queryClient.invalidateQueries({
        queryKey: [`cards`, id],});
      
      // Don't invalidate/refetch the cards query immediately to prevent cards from disappearing
      // The optimistic card is already in the cache with the correct data
      // The cards will be refetched naturally when:
      // - User performs another action (drag, filter, etc.)
      // - Component remounts
      // - Window regains focus (if refetchOnWindowFocus is enabled)
      // This ensures a smooth UX without cards disappearing
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update on error - restore previous cards
      if (context?.previousCards) {
        queryClient.setQueryData([`cards`, id], context.previousCards);
      } else {
        // If no previous cards, remove optimistic card if it exists
        queryClient.setQueryData([`cards`, id], (oldData) => {
          if (!oldData?.data?.data?.responseData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: {
                ...oldData.data.data,
                responseData: oldData.data.data.responseData.filter(
                  (card) => card.card.card_id !== context?.tempCardId
                ),
              },
            },
          };
        });
      }

      console.error("Error creating card", error);
      setLoading(false);
      // Keep field open on error so user can retry or edit
      // Don't close the field - let user decide what to do
    },
  });

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleEnterKeyPress();
    }
    if (event.key === "btnClicked") {
      handleEnterKeyPress();
    }
  };

  const handleEnterKeyPress = () => {
    // Don't allow creating if already loading or title is empty
    if (loading || !title?.trim()) {
      return;
    }

    handleCreateCardClick?.(null, stage.toString());
    const data = {
      title,
      board_id: id,
      ...(milestone && { epic_id: milestone }),
      ...(isTodo
        ? { is_todo: true, todo_column: "Open" }
        : { workflow_step_id: stage }),
      ...(parentCardId ? { parent_card_id: parentCardId } : {}),
    };

    setLoading(true);
    createMutate(data);
  };

  useEffect(() => {
    setCreateBtnOpen(isCreateCardFieldOpen);
    createBtnOpenRef.current = isCreateCardFieldOpen;
  }, [isCreateCardFieldOpen]);

  // Sync title with ref
  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  // Handle tab visibility changes to preserve input field state
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;
      
      // When tab becomes visible again, restore the field state if user had typed something
      if (isVisible && titleRef.current) {
        // Restore field if user had typed something before switching tabs
        setCreateBtnOpen(true);
        createBtnOpenRef.current = true;
        setTitle(titleRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);


  return (
    <div style={isMobile ? { width: "100%" } : {}}>
      {createBtnOpen ? (
        <div
          style={{ marginBottom: "10px", width: isMobile ? "100%" : "auto" }}
        >
          <FieldCreateEditCard
            handleKeyDown={handleKeyDown}
            setTitle={setTitle}
            value={title}
            setCreateBtnOpen={(shouldClose) => {
              // Only close if tab is visible (not switching tabs) or if explicitly requested
              if (shouldClose !== false && isTabVisibleRef.current) {
                setCreateBtnOpen(false);
                createBtnOpenRef.current = false;
              }
            }}
            preventCloseOnTabSwitch={true}
            loading={loading}
            disabled={loading}
          />
          {/* <FormControl fullWidth>
            <TextareaAutosize
              aria-label="empty textarea"
              placeholder="What needs to be done?"
              minRows={4} // Set the minimum number of rows
              className="create-card-textarea"
              onKeyDown={handleKeyDown}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setCreateBtnOpen(false)}
              autoFocus
            />
            <InputAdornment position="end">
              <img src={CheckBox} className=" mb-6" />
              <ExpandMoreIcon className="mb-6" />
            </InputAdornment>
          </FormControl> */}
        </div>
      ) : (
        id &&
        !isMobile && (boardPermission?.can_create_task || boardPermission?.is_admin || isAdmin() || boardName?.is_owner) && (
          <div className="d-flex justify-content-end create-card-div">
            <Tooltip
              title={
                isTodo
                  ? TooltipName.BOARD_PAGE.CREATE_QUICK_TASK
                  : TooltipName.BOARD_PAGE.CREATE_TASK
              }
            >
              <CustomButton
                size="small"
                className="create-card-btn create-card-btn-bg reset-btn"
                onClick={() => {
                  setCreateBtnOpen(true);
                  createBtnOpenRef.current = true;
                }}
              >
                <AddIcon className="create-card-btn-icon" />
              </CustomButton>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
}

export default CreateCard;
